import { Input } from "@/components/ui/input";
import { Link } from "react-router-dom";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { MapPin, Clock, CreditCard, CheckCircle2, Sparkles, Loader2, Heart } from "lucide-react";
import { LOYALTY_ENABLED } from "@/lib/features";
import { PlatterDetails } from "@/components/PlatterDetails";

/** Keep only digits; strip leading '1' (US country code) so the mask always has 10 digits to work with. */
export const digitsOnly = (v) => {
  const d = (v || "").replace(/\D/g, "");
  return d.length === 11 && d.startsWith("1") ? d.slice(1) : d;
};

/** Format a phone string progressively as the user types: "5551234567" → "(555) 123-4567". */
export const formatPhoneUS = (v) => {
  const d = digitsOnly(v).slice(0, 10);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
};

export const isValidPhone = (v) => digitsOnly(v).length === 10;
export const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test((v || "").trim());

/** Returns {valid: bool, errors: {name?, phone?, email?}} for a checkout contact form. */
export function validateContact({ name, phone, email }) {
  const errors = {};
  if (!name?.trim()) errors.name = "Required";
  if (!isValidPhone(phone)) errors.phone = "Enter a 10-digit US phone number";
  if (email && !isValidEmail(email)) errors.email = "Enter a valid email";
  return { valid: Object.keys(errors).length === 0, errors };
}

const Card = ({ title, subtitle, children, testId }) => (
  <div className="bg-white p-6 rounded-2xl border border-neutral-200/70 space-y-4" data-testid={testId}>
    <div><h2 className="font-display font-semibold text-lg">{title}</h2>{subtitle && <p className="text-sm text-neutral-500 mt-1">{subtitle}</p>}</div>
    {children}
  </div>
);

export function ContactSection({ form, setForm, errors = {} }) {
  const onPhone = (e) => setForm({ ...form, phone: formatPhoneUS(e.target.value) });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <Card title="Contact" subtitle="We'll text or email you when your order is ready.">
      <div className="grid sm:grid-cols-3 gap-4">
        <div>
          <Label htmlFor="name">Name</Label>
          <Input id="name" value={form.name} onChange={set("name")} required aria-invalid={!!errors.name} data-testid="checkout-name"/>
          {errors.name && <p className="text-xs text-red-600 mt-1" data-testid="checkout-name-error">{errors.name}</p>}
        </div>
        <div>
          <Label htmlFor="phone">Phone</Label>
          <Input id="phone" type="tel" inputMode="numeric" placeholder="(555) 123-4567"
                 value={form.phone} onChange={onPhone} maxLength={14} required
                 aria-invalid={!!errors.phone} data-testid="checkout-phone"/>
          {errors.phone && <p className="text-xs text-red-600 mt-1" data-testid="checkout-phone-error">{errors.phone}</p>}
        </div>
        <div>
          <Label htmlFor="email">Email (for confirmation)</Label>
          <Input id="email" type="email" placeholder="you@email.com"
                 value={form.email} onChange={set("email")} autoComplete="email"
                 aria-invalid={!!errors.email} data-testid="checkout-email"/>
          {errors.email && <p className="text-xs text-red-600 mt-1" data-testid="checkout-email-error">{errors.email}</p>}
        </div>
      </div>
    </Card>
  );
}

export const tomorrowInTimezone = (timezone) => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: timezone || "America/New_York",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  const localMidnight = Date.UTC(
    Number(values.year),
    Number(values.month) - 1,
    Number(values.day) + 1
  );
  return new Date(localMidnight).toISOString().slice(0, 10);
};

export function PickupSection({ stores, storeId, setStoreId, slots, pickupTime, setPickupTime, pickupDate, setPickupDate, requiresAdvanceDate, storeTimezone, notes, setNotes }) {
  return (
    <Card title="Pickup" subtitle="Online ordering is currently available at the location below. Pickup only." testId="checkout-pickup">
      <div className="space-y-2">
        {stores.map((s) => (
          <button type="button" key={s.id} onClick={() => setStoreId(s.id)} data-testid={`store-choice-${s.id}`}
            className={`w-full text-left p-4 rounded-xl border-2 transition-all flex items-start gap-3 ${storeId === s.id ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/5" : "border-neutral-200 hover:border-neutral-400 bg-white"}`}>
            <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${storeId === s.id ? "bg-[hsl(var(--primary))] text-white" : "bg-neutral-100 text-neutral-600"}`}><MapPin className="w-4 h-4"/></div>
            <div className="min-w-0"><div className="font-semibold">{s.name}</div><div className="text-xs text-neutral-500 mt-0.5">{s.address}</div></div>
          </button>
        ))}
        {stores.length === 0 && <p className="text-sm text-neutral-500" data-testid="no-ordering-stores">Online ordering isn't open yet. Please check back soon.</p>}
      </div>
      <div>
        <Label className="flex items-center gap-1"><Clock className="w-3.5 h-3.5"/>Pickup time</Label>
        <select value={pickupTime} onChange={(e) => setPickupTime(e.target.value)} className="mt-1 h-10 w-full sm:w-64 rounded-lg border border-neutral-200 bg-white text-sm px-3" data-testid="checkout-pickup-time">
          {slots.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
      </div>
      {requiresAdvanceDate && (
        <div className="border border-orange-200 bg-orange-50 p-4" data-testid="checkout-platter-date-section">
          <Label htmlFor="platter-pickup-date" className="font-semibold text-neutral-900">
            Party platter pickup date
          </Label>
          <p className="mt-1 text-xs text-neutral-600">
            Party platters require at least one day’s notice. Same-day pickup is unavailable.
          </p>
          <Input
            id="platter-pickup-date"
            type="date"
            min={tomorrowInTimezone(storeTimezone)}
            value={pickupDate}
            onChange={(event) => setPickupDate(event.target.value)}
            className="mt-3 max-w-xs bg-white"
            required
            data-testid="checkout-platter-pickup-date"
          />
        </div>
      )}
      <div><Label>Notes (optional)</Label><Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Allergies, extra ginger, no wasabi…" data-testid="checkout-notes"/></div>
    </Card>
  );
}

export function TipSection({ subtotal, tip, setTip }) {
  const presets = [0.10, 0.15, 0.20];
  const [custom, setCustom] = useState(false);
  const pct = presets.find((p) => Math.abs(subtotal * p - tip) < 0.005);
  const choose = (p) => { setCustom(false); setTip(Math.round(subtotal * p * 100) / 100); };
  const pill = (active) => `h-11 rounded-xl border-2 text-sm font-semibold transition-colors ${active ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/5 text-[hsl(var(--primary))]" : "border-neutral-200 hover:border-neutral-400"}`;
  return (
    <Card title="Add a tip" subtitle="100% of tips go to the team preparing your order." testId="checkout-tip">
      <div className="grid grid-cols-5 gap-2">
        {presets.map((p) => (
          <button type="button" key={p} onClick={() => choose(p)} className={pill(!custom && pct === p)} data-testid={`tip-${Math.round(p * 100)}`}>
            {Math.round(p * 100)}%<div className="text-[11px] font-normal text-neutral-500">${(subtotal * p).toFixed(2)}</div>
          </button>
        ))}
        <button type="button" onClick={() => setCustom(true)} className={pill(custom)} data-testid="tip-custom">Custom</button>
        <button type="button" onClick={() => { setCustom(false); setTip(0); }} className={pill(!custom && tip === 0)} data-testid="tip-skip">No tip</button>
      </div>
      {custom && (
        <div className="flex items-center gap-2 max-w-xs">
          <span className="text-neutral-500">$</span>
          <Input type="number" min="0" step="0.01" inputMode="decimal" autoFocus value={tip || ""} placeholder="0.00"
            onChange={(e) => setTip(Math.max(0, Math.round((parseFloat(e.target.value) || 0) * 100) / 100))} data-testid="tip-custom-input"/>
        </div>
      )}
      {tip > 0 && <p className="text-xs text-emerald-700 flex items-center gap-1"><Heart className="w-3 h-3 fill-emerald-600 text-emerald-600"/>Thank you! Adding ${tip.toFixed(2)} tip.</p>}
    </Card>
  );
}

export function PaymentSection({ config, cardReady, cardError, containerId }) {
  const cardAvailable = !!config?.enabled;
  return (
    <Card title="Payment" subtitle="Secure card checkout powered by Square." testId="checkout-payment">
      {!cardAvailable && config && <p className="text-sm text-red-600" data-testid="payments-unavailable">Online payments are temporarily unavailable. Please try again shortly or call the store.</p>}
      {cardAvailable && (
        <div className="pt-2">
          <div id={containerId} className="min-h-[52px]" data-testid="square-card-container"/>
          {!cardReady && !cardError && <p className="text-xs text-neutral-400 flex items-center gap-1 mt-1"><Loader2 className="w-3 h-3 animate-spin"/>Loading secure card form…</p>}
          {cardError && <p className="text-xs text-red-600 mt-1" data-testid="square-card-error">{cardError}</p>}
          <div className="flex items-center justify-between flex-wrap gap-2 mt-3">
            <p className="text-[11px] text-neutral-500" data-testid="checkout-cards-accepted">We accept Visa, Mastercard, American Express, Discover.</p>
            <p className="text-[11px] text-neutral-400">CVV adapts to your card (3 digits standard · 4 digits for AmEx).</p>
          </div>
          {config.environment === "sandbox" && <p className="text-[11px] text-neutral-400 mt-2">Test mode · use card 4111 1111 1111 1111, any future date, CVV 111.</p>}
        </div>
      )}
    </Card>
  );
}

export function OrderSummary({ items, subtotal, discount, tax, tip = 0, total, pointsEarned, user, submitLabel, disabled, busy, unavailable = [], storeName, onRemoveUnavailable }) {
  const blockedIds = new Set(unavailable.map((l) => l.line_id || l.item_id));
  return (
    <aside className="bg-white p-6 rounded-2xl border border-neutral-200/70 h-fit lg:sticky lg:top-24">
      <h2 className="font-display font-semibold text-lg mb-4">Your order</h2>
      {unavailable.length > 0 && (
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-3" data-testid="checkout-unavailable-warning">
          <p className="text-sm font-semibold text-red-700">
            Not available{storeName ? ` at ${storeName}` : ""}
          </p>
          <ul className="mt-2 space-y-1">
            {unavailable.map((line) => (
              <li key={line.line_id || line.item_id} className="flex items-center justify-between gap-2 text-xs text-red-700">
                <span className="truncate">{line.name}</span>
                {onRemoveUnavailable && (
                  <button type="button" onClick={() => onRemoveUnavailable(line.line_id)} className="font-semibold underline shrink-0"
                    data-testid={`remove-unavailable-${line.line_id || line.item_id}`}>Remove</button>
                )}
              </li>
            ))}
          </ul>
          <p className="mt-2 text-xs text-red-600">Remove these items or choose another pickup location to continue.</p>
        </div>
      )}
      <div className="space-y-3 mb-4 max-h-64 overflow-y-auto">
        {items.map((it) => (
          <div key={it.line_id || it.item_id} className="text-sm">
            <div className="flex justify-between">
              <span className="mr-2 truncate text-neutral-600">{it.quantity}× {it.name}</span>
              <span>${(it.price * it.quantity).toFixed(2)}</span>
            </div>
            {blockedIds.has(it.line_id || it.item_id) && (
              <p className="text-xs font-semibold text-red-600" data-testid={`summary-unavailable-${it.line_id || it.item_id}`}>Unavailable at this location</p>
            )}
            <PlatterDetails platter={it.platter} compact />
          </div>
        ))}
      </div>
      <div className="border-t border-neutral-200 pt-3 space-y-1 text-sm">
        <div className="flex justify-between text-neutral-500"><span>Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
        {LOYALTY_ENABLED && discount > 0 && <div className="flex justify-between text-[hsl(var(--primary))]"><span>Reward</span><span>−${discount.toFixed(2)}</span></div>}
        <div className="flex justify-between text-neutral-500"><span>Tax</span><span>${tax.toFixed(2)}</span></div>
        {tip > 0 && <div className="flex justify-between text-neutral-500"><span>Tip</span><span data-testid="checkout-tip-amount">${tip.toFixed(2)}</span></div>}
        <div className="flex justify-between font-display font-bold text-lg pt-2"><span>Total</span><span data-testid="checkout-total">${total.toFixed(2)}</span></div>
        {LOYALTY_ENABLED && user && pointsEarned > 0 && <div className="text-xs text-emerald-600 pt-1">Earn +{pointsEarned} pts on this order</div>}
      </div>
      <Button type="submit" disabled={disabled} className="w-full mt-6 rounded-full h-12 text-base bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90" data-testid="place-order-btn">
        {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin"/>Processing…</> : submitLabel}
      </Button>
      <p className="mt-3 text-center text-[11px] text-neutral-400">
        By placing this order, you agree to the <Link to="/terms" className="underline">Terms & Conditions</Link>{" "}
        and <Link to="/privacy-policy" className="underline">Privacy Policy</Link>.
      </p>
    </aside>
  );
}

export function Confirmation({ placed, store, onMore, onOrders, user }) {
  return (
    <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="order-confirmation">
      <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-6"/>
      <h1 className="font-display font-bold text-4xl tracking-tight">Order Confirmed!</h1>
      <p className="mt-3 text-neutral-500">Thanks {placed.customer_name}. {placed.payment_status === "paid" ? "Your payment went through and" : ""} your order is being prepared.</p>
      <div className="mt-8 p-6 bg-white rounded-2xl border border-neutral-200 text-left space-y-3">
        <div className="flex justify-between"><span className="text-neutral-500">Order #</span><span className="font-mono font-semibold" data-testid="order-number">{placed.order_number}</span></div>
        <div className="flex justify-between"><span className="text-neutral-500">Pickup at</span><span className="font-medium text-right">{store?.name || placed.location}<br/><span className="text-xs text-neutral-500 font-normal">{store?.address}</span></span></div>
        <div className="flex justify-between"><span className="text-neutral-500">Pickup time</span><span className="font-medium" data-testid="confirm-pickup-time">{placed.pickup_time === "ASAP" ? "ASAP (about 20 min)" : placed.pickup_time}</span></div>
        {placed.pickup_date && <div className="flex justify-between"><span className="text-neutral-500">Pickup date</span><span className="font-medium" data-testid="confirm-pickup-date">{placed.pickup_date}</span></div>}
        <div className="flex justify-between"><span className="text-neutral-500">Payment</span><span className="font-medium" data-testid="confirm-payment">{placed.payment_status === "paid" ? `Paid · ${placed.card_brand || "card"} ****${placed.card_last4 || ""}` : "Pending"}</span></div>
        {placed.tip > 0 && <div className="flex justify-between"><span className="text-neutral-500">Tip</span><span className="font-medium" data-testid="confirm-tip">${placed.tip.toFixed(2)}</span></div>}
        <div className="flex justify-between font-display font-bold text-lg border-t border-neutral-100 pt-3"><span>Total</span><span>${placed.total.toFixed(2)}</span></div>
        {LOYALTY_ENABLED && placed.points_earned > 0 && <div className="pt-3 border-t border-neutral-200 flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 rounded-lg p-3"><Sparkles className="w-4 h-4"/>You earned <span className="font-bold">+{placed.points_earned} pts</span></div>}
      </div>
      {placed.customer_email && <p className="text-xs text-neutral-500 mt-4">A confirmation was sent to {placed.customer_email}.</p>}
      <div className="flex gap-3 justify-center mt-8">
        <Button onClick={onMore} variant="outline" className="rounded-full">Order More</Button>
        {user && <Button onClick={onOrders} className="rounded-full bg-[hsl(var(--primary))]">View My Orders</Button>}
      </div>
    </div>
  );
}
